import React, { useState, useEffect } from "react";
import { DataGrid, GridToolbarContainer } from "@mui/x-data-grid";
import { Button, Checkbox, FormControlLabel, Box, Typography } from "@mui/material";
import { useSnack } from "../../services/SnakbarProvider";
import { handleError } from "../../services/errorHandler";
import { getStock, StockDto } from "../../api/stockmoves/index.ts";
import { freezeStock, exportCSV } from "../../api/physicalInventoryCounts/index.ts";
import { generateInventoryTags, reprintInventoryTags, getTagsByWarehouse } from "../../api/inventoryTags/index.ts";
import { esES } from "@mui/x-data-grid/locales";

interface PICDashboardProps {
  picId: number;
  warehouseId: number;
  companyId: number;
  statusCode?: string;
  onRefresh?: () => void;
}

const PICDashboard: React.FC<PICDashboardProps> = ({
  picId,
  warehouseId,
  companyId,
  statusCode,
  onRefresh,
}) => {
  const [rows, setRows] = useState<any[]>([]);
  const [selectedRows, setSelectedRows] = useState<number[]>([]);
  const [printAll, setPrintAll] = useState(false);
  const [loading, setLoading] = useState(false);
  const { openSnack } = useSnack();

  const fetchStock = async () => {
    setLoading(true);
    try {
      const stockData = await getStock(warehouseId);
      const rowsWithId = stockData.map((item, index) => ({
        id: item.productId,
        productId: item.productId,
        productCode: item.productCode,
        productDescription: item.productDescription || "",
        stockQuantity: item.stockQuantity,
      }));
      setRows(rowsWithId);
      
      // Default select rows with stock > 0
      const rowsWithStock = rowsWithId
        .filter((row) => row.stockQuantity > 0)
        .map((row) => row.id);
      setSelectedRows(rowsWithStock);
    } catch (error) {
      openSnack(handleError(error), "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStock();
  }, [warehouseId]);

  const handleFreezeStock = async () => {
    try {
      setLoading(true);
      await freezeStock(picId);
      openSnack("Stock congelado correctamente", "success");
      if (onRefresh) {
        onRefresh();
      }
    } catch (error) {
      openSnack(handleError(error), "error");
    } finally {
      setLoading(false);
    }
  };

  const handlePrintTags = async () => {
    try {
      setLoading(true);
      
      let productIdsToCheck: number[] | undefined;
      if (!printAll) {
        // Print only selected products
        if (selectedRows.length === 0) {
          openSnack("Seleccione al menos un producto", "warning");
          return;
        }
        productIdsToCheck = selectedRows;
      }
      
      // Check if tags already exist for this warehouse and company (and selected products if applicable)
      const existingTagsResponse = await getTagsByWarehouse(warehouseId, companyId, productIdsToCheck);
      const existingTags = existingTagsResponse.data;
      
      let response;
      if (existingTags.length > 0) {
        // Tags already exist, reprint them
        const tagCodes = existingTags.map(tag => tag.code);
        response = await reprintInventoryTags({ codes: tagCodes });
        openSnack("Marbetes reimpresos correctamente", "success");
      } else {
        // No tags exist, generate new ones
        response = await generateInventoryTags({
          warehouseIds: [warehouseId],
          emptyTagsCount: 0,
          companyId: companyId,
        });
        openSnack("Marbetes generados correctamente", "success");
      }
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `inventory_tags_${Date.now()}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      openSnack(handleError(error), "error");
    } finally {
      setLoading(false);
    }
  };

  const handleExportCSV = async () => {
    try {
      setLoading(true);
      const response = await exportCSV(picId);
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `pic_export_${Date.now()}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      openSnack("CSV exportado correctamente", "success");
    } catch (error) {
      openSnack(handleError(error), "error");
    } finally {
      setLoading(false);
    }
  };

  const columns = [
    {
      field: "productCode",
      headerName: "Código",
      flex: 0.2,
    },
    {
      field: "productDescription",
      headerName: "Descripción",
      flex: 0.4,
    },
    {
      field: "stockQuantity",
      headerName: "Stock",
      flex: 0.2,
      type: "number",
    },
  ];

  const CustomToolbar = () => {
    return (
      <GridToolbarContainer>
        <Button
          color="primary"
          variant="contained"
          onClick={handlePrintTags}
          disabled={loading}
          sx={{ mr: 1 }}
        >
          Imprimir Marbete
        </Button>
        <FormControlLabel
          control={
            <Checkbox
              checked={printAll}
              onChange={(e) => setPrintAll(e.target.checked)}
            />
          }
          label="Imprimir Todo"
          sx={{ mr: 1 }}
        />
        {statusCode !== "PIC_FIXED" && (
          <Button
            color="secondary"
            variant="contained"
            onClick={handleFreezeStock}
            disabled={loading}
            sx={{ mr: 1 }}
          >
            Fijar Existencias
          </Button>
        )}
        {statusCode === "PIC_FIXED" && (
          <Button
            color="success"
            variant="contained"
            onClick={handleExportCSV}
            disabled={loading}
          >
            Descargar CSV
          </Button>
        )}
      </GridToolbarContainer>
    );
  };

  return (
    <Box sx={{ height: "100%", width: "100%" }}>
      <DataGrid
        rows={rows}
        columns={columns}
        loading={loading}
        checkboxSelection
        rowSelectionModel={selectedRows}
        onRowSelectionModelChange={(newSelection) => {
          setSelectedRows(newSelection as number[]);
        }}
        slots={{
          toolbar: CustomToolbar,
        }}
        localeText={esES.components.MuiDataGrid.defaultProps.localeText}
        sx={{
          "& .MuiDataGrid-cell": {
            display: "flex",
            alignItems: "center",
          },
        }}
      />
    </Box>
  );
};

export default PICDashboard;

